import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { usersAPI, notificationsAPI, storeManagerAPI } from '../api';
import {
  SquaresFour,
  ChartBar as BarChart3,
  Package as PhPackage,
  Scissors,
  UsersThree,
  UserList,
  Receipt,
  Monitor,
  SignOut,
  List as HamburgerMenu,
  X,
  ShieldCheck,
  Sun,
  Moon,
  CaretRight,
  UserCheck,
  Calendar,
  Clock,
  Camera,
  Eye,
  EyeSlash,
  FloppyDisk,
  Trash,
  WarningCircle,
  Check,
  User,
  Lock,
  Key,
  Info,
  DotsThreeOutline,
  Buildings,
  Storefront as StorefrontIcon,
  Bell,
  Wallet,
} from '@phosphor-icons/react';
import SubscriptionBanner from './SubscriptionBanner';

const ManagerLayout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const { currentBranch } = useBranch();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('pos-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  // Toggle dark class on html element for Tailwind dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('pos-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);

  const [currentTime, setCurrentTime] = useState(new Date());

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({
    username: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const profileImageRef = useRef(null);

  // Notification bell state
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifList, setNotifList] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);
  const [stockAlerts, setStockAlerts] = useState({ lowStock: 0, outOfStock: 0 });
  const notifRef = useRef(null);

  // 24-hour snooze helpers
  const SNOOZE_KEY = 'stock-alert-snooze';
  const isSnoozed = (key) => {
    try { const s = JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}'); return s[key] && Date.now() < s[key]; }
    catch { return false; }
  };
  const snooze24h = (key) => {
    try {
      const s = JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}');
      s[key] = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem(SNOOZE_KEY, JSON.stringify(s));
      setStockAlerts(a => ({ ...a, [key]: 0 }));
    } catch {}
  };

  const fetchNotifs = () => {
    notificationsAPI.list().then(res => setNotifList(res.data.notifications || [])).catch(() => {});
    storeManagerAPI.getNotifications().then(res => {
      setStockAlerts({ lowStock: res.data.lowStock || 0, outOfStock: res.data.outOfStock || 0 });
    }).catch(() => {});
  };

  const handleMarkRead = async (id) => {
    await notificationsAPI.markRead(id).catch(() => {});
    setNotifList(prev => prev.filter(n => n.id !== id));
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    await notificationsAPI.markAllRead().catch(() => {});
    setNotifList([]);
    setMarkingAll(false);
  };

  // Close notif dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  // Get tenant slug for navigation
  const tenantSlug = slug || user?.tenantSlug || '';
  const isOnPOSPage = location.pathname.includes('/pos');

  const handleLaunchPOS = () => navigate(`/${tenantSlug}/manager/pos`);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    logout();
    navigate(`/${tenantSlug}/login`);
  };

  // Profile handlers
  const openProfileModal = () => {
    setProfileData({
      username: user?.username || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setProfileImage(null);
    setProfileImagePreview(user?.profileImage || null);
    setProfileError('');
    setProfileSuccess('');
    setActiveTab('profile');
    setShowProfileModal(true);
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setProfileError('Image must be less than 5MB');
        return;
      }
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUploadProfileImage = async () => {
    if (!profileImage) return;
    setProfileLoading(true);
    setProfileError('');
    try {
      const formData = new FormData();
      formData.append('image', profileImage);
      await usersAPI.uploadProfileImage(formData);
      setProfileSuccess('Profile image updated!');
      setProfileImage(null);
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to upload image');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      await usersAPI.deleteProfileImage();
      setProfileImagePreview(null);
      setProfileSuccess('Profile image removed');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to delete image');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileSuccess('');

    if (profileData.newPassword) {
      if (!profileData.currentPassword) {
        setProfileError('Current password is required to change password');
        return;
      }
      if (profileData.newPassword !== profileData.confirmPassword) {
        setProfileError('New passwords do not match');
        return;
      }
      if (profileData.newPassword.length < 6) {
        setProfileError('New password must be at least 6 characters');
        return;
      }
    }

    setProfileLoading(true);
    try {
      const updateData = {};
      if (profileData.username !== user?.username) {
        updateData.username = profileData.username;
      }
      if (profileData.newPassword) {
        updateData.currentPassword = profileData.currentPassword;
        updateData.newPassword = profileData.newPassword;
      }

      if (Object.keys(updateData).length === 0) {
        setProfileError('No changes to save');
        setProfileLoading(false);
        return;
      }

      await usersAPI.updateProfile(updateData);
      await refreshUser();
      setProfileSuccess('Profile updated successfully!');
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Active state – exact match or prefix match
  const isActive = (item) => {
    if (item.matchPrefix) return location.pathname.startsWith(item.matchPrefix);
    return location.pathname === item.path;
  };

  const isServicesType = ['SERVICES', 'SALON'].includes(user?.businessType);
  const isSplitStorefront = user?.storefrontMode === 'SPLIT';

  // Icon components
  const DashboardIcon = (props) => <SquaresFour size={20} weight="bold" className={props.className} />;
  const SalesIcon = (props) => <BarChart3 size={20} weight="bold" className={props.className} />;
  const InventoryIcon = (props) => isServicesType
    ? <Scissors size={20} weight="bold" className={props.className} />
    : <PhPackage size={20} weight="bold" className={props.className} />;
  const TeamIcon = (props) => <UsersThree size={20} weight="bold" className={props.className} />;
  const CustomersIcon = (props) => <UserList size={20} weight="bold" className={props.className} />;
  const ExpensesIcon = (props) => <Wallet size={20} weight="bold" className={props.className} />;
  const StoreNavIcon = (props) => <StorefrontIcon size={20} weight="bold" className={props.className} />;

  // Flat nav items (like owner)
  const navItems = [
    { path: `/${tenantSlug}/manager/dashboard`, label: 'Dashboard', icon: DashboardIcon },
    { path: `/${tenantSlug}/manager/sales`, label: 'Sales', icon: SalesIcon, matchPrefix: `/${tenantSlug}/manager/sales` },
    { path: `/${tenantSlug}/manager/inventory`, label: isServicesType ? 'Services' : 'Inventory', icon: InventoryIcon, matchPrefix: `/${tenantSlug}/manager/inventory` },
    { path: `/${tenantSlug}/manager/team`, label: 'Team', icon: TeamIcon, matchPrefix: `/${tenantSlug}/manager/team` },
    { path: `/${tenantSlug}/manager/customers`, label: 'Customers', icon: CustomersIcon, matchPrefix: `/${tenantSlug}/manager/customers` },
    { path: `/${tenantSlug}/manager/expenses`, label: 'Expenses', icon: ExpensesIcon },
    ...(isSplitStorefront ? [{ path: `/${tenantSlug}/manager/store`, label: 'My Store', icon: StoreNavIcon }] : []),
  ];

  const bgClass = darkMode ? 'bg-primaryB-900' : 'bg-primaryB-50';
  const surfaceClass = darkMode ? 'bg-primaryB-800' : 'bg-white';
  const textClass = darkMode ? 'text-white' : 'text-primaryB-900';
  const mutedClass = darkMode ? 'text-primaryB-400' : 'text-primaryB-500';
  const borderClass = darkMode ? 'border-primaryB-700/40' : 'border-primaryB-200/60';

  return (
    <div data-theme={darkMode ? 'dark' : 'light'} className={`h-screen-dvh overflow-hidden flex ${bgClass} ${textClass} safe-area-top`}>
      {/* Sidebar – Full Height Desktop */}
      <aside className={`hidden lg:flex ${sidebarCollapsed ? 'w-20' : 'w-64'} ${surfaceClass} flex-col shrink-0 transition-all duration-300 overflow-hidden shadow-card dark:shadow-card-dark`}>

        {/* Sidebar Top: Branding + Collapse */}
        <div className={`shrink-0 h-14 sm:h-16 short:h-12 border-b ${borderClass} flex items-center justify-between ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
          <div className={`flex items-center ${sidebarCollapsed ? '' : 'gap-2.5'} min-w-0`}>
            <div className="w-8 h-8 bg-primaryB-950 rounded-xl flex items-center justify-center shadow-card shrink-0 short:w-7 short:h-7">
              <ShieldCheck weight="bold" className="w-4 h-4 text-accent-400 short:w-3.5 short:h-3.5" />
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <h1 className={`text-sm font-bold tracking-tight uppercase ${textClass} truncate`}>Manager Hub</h1>
                <p className="text-[9px] font-bold text-accent-500 uppercase tracking-wider truncate mt-0.5">{user?.tenantName}</p>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`shrink-0 p-1.5 rounded-xl ${mutedClass} hover:bg-primaryB-50 dark:hover:bg-primaryB-700 transition-all duration-200`}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <CaretRight weight="bold" className={`w-4 h-4 transition-transform duration-200 ${sidebarCollapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Nav – flat list */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          <div className="space-y-0.5">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link key={item.path} to={item.path}
                  className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl text-xs font-bold uppercase transition-all duration-200 ${
                    active
                      ? darkMode ? 'bg-accent-500/15 text-accent-400' : 'bg-[#373a3f] text-accent-400 shadow-elevated'
                      : `${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-primaryB-50 hover:text-primaryB-900'}`
                  }`}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer */}
        <div className={`shrink-0 border-t ${borderClass} p-2.5 space-y-1`}>
          {!sidebarCollapsed && (
            <div className={`flex items-center rounded-xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} p-1 mb-1`}>
              <button
                onClick={() => setDarkMode(false)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${!darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
              >
                <Sun weight="bold" className="w-3.5 h-3.5" />
                Light
              </button>
              <button
                onClick={() => setDarkMode(true)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
              >
                <Moon weight="bold" className="w-3.5 h-3.5" />
                Dark
              </button>
            </div>
          )}
          {sidebarCollapsed && (
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`w-full flex items-center justify-center p-2 rounded-xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} ${textClass} mb-1`}
              title={darkMode ? 'Switch to Light' : 'Switch to Dark'}
            >
              {darkMode ? <Sun weight="bold" className="w-4 h-4" /> : <Moon weight="bold" className="w-4 h-4" />}
            </button>
          )}
          <Link to={`/${tenantSlug}/manager/pos`}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-white text-xs font-bold uppercase shadow-elevated shadow-accent-500/20 transition-all duration-200`}
            title={sidebarCollapsed ? 'Launch POS' : ''}
          >
            <Monitor weight="bold" className="w-5 h-5 shrink-0" />
            {!sidebarCollapsed && <span>Launch POS</span>}
          </Link>
          <button onClick={handleLogout}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-xl text-xs font-bold uppercase ${mutedClass} hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200`}
            title={sidebarCollapsed ? 'Logout' : ''}
          >
            <SignOut weight="bold" className="w-5 h-5 shrink-0" />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
          <div className={`w-72 h-full ${surfaceClass} flex flex-col overflow-hidden`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center gap-3 px-5 border-b ${borderClass} pt-6 pb-4 shrink-0`}>
              <div className="w-8 h-8 bg-primaryB-950 rounded-xl flex items-center justify-center shadow-card">
                <ShieldCheck weight="bold" className="w-4 h-4 text-accent-400" />
              </div>
              <div>
                <p className={`text-sm font-bold tracking-tight uppercase ${textClass}`}>Manager Hub</p>
                <p className="text-[9px] font-bold text-accent-500 uppercase tracking-wider">{user?.tenantName}</p>
              </div>
            </div>
            <nav className="flex-1 overflow-y-auto py-3 px-3">
              <div className="space-y-0.5">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  return (
                    <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold uppercase transition-all duration-200 ${
                        active
                          ? darkMode ? 'bg-accent-500/15 text-accent-400' : 'bg-[#373a3f] text-accent-400 shadow-elevated'
                          : `${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-primaryB-50 hover:text-primaryB-900'}`
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </nav>
            <div className={`shrink-0 border-t ${borderClass} p-3 space-y-1`}>
              <div className={`flex items-center rounded-xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} p-1 mb-1`}>
                <button
                  onClick={() => setDarkMode(false)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${!darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                >
                  <Sun weight="bold" className="w-3.5 h-3.5" />Light
                </button>
                <button
                  onClick={() => setDarkMode(true)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                >
                  <Moon weight="bold" className="w-3.5 h-3.5" />Dark
                </button>
              </div>
              <Link to={`/${tenantSlug}/manager/pos`} onClick={() => setMobileMenuOpen(false)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-white text-xs font-bold uppercase shadow-elevated shadow-accent-500/20 transition-all duration-200"
              >
                <Monitor weight="bold" className="w-5 h-5" /><span>Launch POS</span>
              </Link>
              <button onClick={handleLogout}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-bold uppercase ${mutedClass} hover:text-red-500 hover:bg-red-50 transition-all duration-200`}
              >
                <SignOut weight="bold" className="w-5 h-5" /><span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Column */}
      <div className="flex flex-col flex-1 overflow-hidden">
        <SubscriptionBanner />

        {/* Header */}
        <header className={`h-14 sm:h-16 short:h-12 ${darkMode ? 'bg-primaryB-900/60' : 'bg-[#F7F7F5]/80'} backdrop-blur-xl border-b ${borderClass} flex items-center justify-between px-4 sm:px-5 short:px-3 shrink-0 z-10`}>
          <div className="flex items-center gap-2">
            {/* Branch indicator */}
            {currentBranch && (
              <div className={`lg:hidden flex items-center gap-1.5 px-2.5 py-1.5 rounded-2xl border ${borderClass} ${surfaceClass}`}>
                <Buildings weight="bold" className={`w-3.5 h-3.5 shrink-0 ${darkMode ? 'text-accent-400' : 'text-primaryB-600'}`} />
                <span className={`text-xs font-bold truncate max-w-[120px] sm:max-w-[180px] ${textClass}`}>
                  {currentBranch.name}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 sm:gap-2.5 ml-auto">
            {/* Date / Time */}
            <div className="hidden lg:flex items-center gap-2">
              <span className={`text-[11px] font-medium tracking-wide ${mutedClass}`}>
                {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
              <span className={`text-[11px] ${darkMode ? 'text-primaryB-600' : 'text-primaryB-300'}`}>·</span>
              <span className={`text-[11px] font-medium tracking-wide ${mutedClass}`}>
                {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>

            {/* Dark mode toggle */}
            <button onClick={() => setDarkMode(!darkMode)}
              className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl hidden sm:flex items-center justify-center ${mutedClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
            >
              {darkMode ? <Sun className="w-4 h-4 sm:w-[18px] sm:h-[18px]" /> : <Moon className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
            </button>

            {/* Notification Bell */}
            <div ref={notifRef} className="relative">
              {(() => {
                const unread = notifList.filter(n => !n.isRead).length
                  + (!isSnoozed('lowStock') && stockAlerts.lowStock > 0 ? 1 : 0)
                  + (!isSnoozed('outOfStock') && stockAlerts.outOfStock > 0 ? 1 : 0);
                const NOTIF_ICONS = {
                  STOREFRONT_ORDER: { emoji: '🛍️', bg: 'bg-blue-100 dark:bg-blue-900/30' },
                  VOID_REQUEST: { emoji: '⚠️', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
                  LOW_STOCK: { emoji: '📦', bg: 'bg-orange-100 dark:bg-orange-900/30' },
                  OUT_OF_STOCK: { emoji: '🚫', bg: 'bg-red-100 dark:bg-red-900/30' },
                };
                const timeAgo = (d) => {
                  const m = Math.floor((Date.now() - new Date(d)) / 60000);
                  if (m < 1) return 'just now';
                  if (m < 60) return `${m}m ago`;
                  const h = Math.floor(m / 60);
                  if (h < 24) return `${h}h ago`;
                  return `${Math.floor(h / 24)}d ago`;
                };
                return (
                  <>
                    <button
                      onClick={() => { setNotifOpen(o => !o); if (!notifOpen) fetchNotifs(); }}
                      className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center relative ${mutedClass} ${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} transition-all duration-200`}
                      title="Notifications"
                    >
                      <Bell className="w-4 h-4 sm:w-[18px] sm:h-[18px]" weight={unread > 0 ? 'fill' : 'bold'} />
                      {unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                          {unread > 9 ? '9+' : unread}
                        </span>
                      )}
                    </button>
                    {notifOpen && (
                      <div className={`fixed top-[60px] inset-x-2 sm:absolute sm:top-auto sm:inset-x-auto sm:right-0 sm:w-80 sm:mt-2 ${surfaceClass} border ${borderClass} rounded-2xl shadow-elevated dark:shadow-elevated-dark z-50 overflow-hidden`}>
                        <div className={`px-4 py-3 border-b ${borderClass} flex items-center justify-between`}>
                          <p className={`text-sm font-bold ${textClass}`}>Notifications</p>
                          {unread > 0 && (
                            <button onClick={handleMarkAllRead} disabled={markingAll}
                              className="text-xs font-semibold text-accent-500 hover:text-accent-600 transition-colors disabled:opacity-50">
                              Mark all read
                            </button>
                          )}
                        </div>
                        <div className="max-h-80 overflow-y-auto">
                          {!isSnoozed('lowStock') && stockAlerts.lowStock > 0 && (
                            <div className={`flex items-center gap-3 px-4 py-3 border-b ${borderClass}`}>
                              <span className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center flex-shrink-0 text-base">📦</span>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-semibold ${textClass}`}>{stockAlerts.lowStock} Low Stock</p>
                                <p className={`text-xs ${mutedClass}`}>Below reorder threshold</p>
                              </div>
                              <button onClick={() => snooze24h('lowStock')} className={`text-[10px] font-semibold px-2 py-1 ${mutedClass} flex-shrink-0`}>Snooze 24h</button>
                            </div>
                          )}
                          {!isSnoozed('outOfStock') && stockAlerts.outOfStock > 0 && (
                            <div className={`flex items-center gap-3 px-4 py-3 border-b ${borderClass}`}>
                              <span className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center flex-shrink-0 text-base">🚫</span>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-semibold ${textClass}`}>{stockAlerts.outOfStock} Out of Stock</p>
                                <p className={`text-xs ${mutedClass}`}>No inventory remaining</p>
                              </div>
                              <button onClick={() => snooze24h('outOfStock')} className={`text-[10px] font-semibold px-2 py-1 ${mutedClass} flex-shrink-0`}>Snooze 24h</button>
                            </div>
                          )}
                          {notifList.length === 0 && stockAlerts.lowStock === 0 && stockAlerts.outOfStock === 0 ? (
                            <div className="px-4 py-6 text-center">
                              <p className={`text-sm ${mutedClass}`}>All clear – no notifications yet</p>
                            </div>
                          ) : (
                            notifList.map(n => {
                              const cfg = NOTIF_ICONS[n.type] || { emoji: '🔔', bg: 'bg-neutral-100 dark:bg-neutral-800' };
                              return (
                                <button key={n.id}
                                  onClick={() => { handleMarkRead(n.id); setNotifOpen(false); }}
                                  className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-primaryB-50 dark:hover:bg-primaryB-800 ${!n.isRead ? (darkMode ? 'bg-primaryB-900' : 'bg-accent-50/50') : ''}`}
                                >
                                  <span className={`w-8 h-8 ${cfg.bg} rounded-xl flex items-center justify-center flex-shrink-0 text-base mt-0.5`}>{cfg.emoji}</span>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <p className={`text-sm font-semibold ${textClass} truncate flex-1`}>{n.title}</p>
                                      {!n.isRead && <span className="w-2 h-2 bg-accent-500 rounded-full flex-shrink-0" />}
                                    </div>
                                    <p className={`text-xs ${mutedClass} truncate`}>{n.message}</p>
                                    <p className={`text-[10px] ${mutedClass} mt-0.5`}>{timeAgo(n.createdAt)}</p>
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Profile avatar */}
            <button onClick={openProfileModal}
              className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full border-2 ${darkMode ? 'border-primaryB-600' : 'border-primaryB-200'} bg-primaryB-100 dark:bg-primaryB-700 overflow-hidden hover:border-accent-400 transition-all duration-200`}
              title="Edit Profile"
            >
              {user?.profileImage ? (
                <img src={user.profileImage} alt={user.fullName} className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-200'} flex items-center justify-center text-xs font-bold ${textClass}`}>
                  {user?.fullName?.charAt(0) || 'M'}
                </div>
              )}
            </button>
          </div>
        </header>

        {/* Main content */}
        <main id="main-content" className={`flex-1 overflow-y-auto p-4 pb-32 sm:p-6 sm:pb-32 lg:p-8 lg:pb-0 short:p-3 short:pb-32 short:lg:pb-0 page-gradient-warm`}>
          {React.cloneElement(children, { darkMode, surfaceClass, textClass, mutedClass, borderClass, bgClass })}
        </main>

        {/* Mobile Bottom Tab Bar */}
        <div className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 ${surfaceClass} border-t ${borderClass} shadow-elevated dark:shadow-elevated-dark safe-area-bottom`}>
          <div className="flex items-center justify-around px-1 py-1.5">
            {[
              { path: `/${tenantSlug}/manager/dashboard`, label: 'Home', icon: SquaresFour },
              { path: `/${tenantSlug}/manager/sales`, label: 'Sales', icon: BarChart3, matchPrefix: `/${tenantSlug}/manager/sales` },
              { path: 'pos', label: 'POS', icon: Monitor, action: handleLaunchPOS, highlight: isOnPOSPage },
              { path: `/${tenantSlug}/manager/inventory`, label: isServicesType ? 'Services' : 'Inventory', icon: isServicesType ? Scissors : PhPackage, matchPrefix: `/${tenantSlug}/manager/inventory` },
              { path: 'more', label: 'More', icon: DotsThreeOutline, action: () => setMoreSheetOpen(true) },
            ].map((tab) => {
              const isTabActive = tab.highlight ||
                (tab.path !== 'pos' && tab.path !== 'more' &&
                  (tab.matchPrefix ? location.pathname.startsWith(tab.matchPrefix) : location.pathname === tab.path));
              return (
                <button
                  key={tab.label}
                  onClick={() => { if (tab.action) { tab.action(); } else { navigate(tab.path); } }}
                  className={`flex flex-col items-center justify-center flex-1 py-1.5 rounded-xl transition-all duration-200 ${
                    isTabActive ? darkMode ? 'text-accent-400' : 'text-[#373A3F]' : mutedClass
                  }`}
                >
                  <tab.icon weight={isTabActive ? 'fill' : 'bold'} className="w-[22px] h-[22px]" />
                  <span className={`text-[10px] font-bold mt-0.5 ${isTabActive ? (darkMode ? 'text-accent-400' : 'text-[#373A3F]') : ''}`}>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Profile Modal */}
      {showProfileModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
          onClick={() => setShowProfileModal(false)}
        >
          <div className="absolute inset-0 bg-primaryB-900/70 backdrop-blur-md" />
          <div
            className={`relative w-full max-w-lg ${surfaceClass} rounded-3xl shadow-elevated border ${borderClass} max-h-[90vh] overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Premium Header */}
            <div className="relative overflow-hidden">
              <div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-br from-emerald-600 via-teal-500 to-cyan-500' : 'bg-gradient-to-br from-emerald-500 via-teal-400 to-cyan-400'}`} />
              <div className="relative px-5 pt-4 pb-12">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-2xl bg-primaryB-50/20 backdrop-blur-sm flex items-center justify-center">
                      <ShieldCheck weight="bold" className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white tracking-tight">Manager Profile</h2>
                      <p className="text-white/70 text-[11px] font-semibold">{user?.tenantName}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className="w-8 h-8 rounded-xl bg-primaryB-50/10 hover:bg-primaryB-50/20 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-200"
                  >
                    <X weight="bold" className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {/* Profile Image */}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2">
                <div className="relative group">
                  <div className={`w-20 h-20 rounded-3xl border-3 ${darkMode ? 'border-primaryB-800' : 'border-white'} shadow-elevated overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-500`}>
                    {profileImagePreview ? (
                      <img src={profileImagePreview} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">{user?.fullName?.charAt(0) || 'M'}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => profileImageRef.current?.click()}
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white shadow-elevated shadow-emerald-500/50 flex items-center justify-center transition-all duration-200 hover:scale-110"
                  >
                    <Camera weight="bold" className="w-3.5 h-3.5" />
                  </button>
                  <input type="file" ref={profileImageRef} onChange={handleProfileImageChange} accept="image/*" className="hidden" />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="pt-14 px-4 sm:px-6 pb-4 overflow-y-auto flex-1">
              {profileImage && (
                <div className="flex justify-center gap-2 mb-4">
                  <button onClick={handleUploadProfileImage} disabled={profileLoading}
                    className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-2xl text-xs font-bold shadow-elevated disabled:opacity-50 flex items-center gap-1.5 transition-all duration-200"
                  >
                    <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
                    {profileLoading ? 'Uploading...' : 'Save Image'}
                  </button>
                  <button
                    onClick={() => { setProfileImage(null); setProfileImagePreview(user?.profileImage || null); }}
                    className={`px-4 py-2 ${surfaceClass} border ${borderClass} rounded-2xl text-xs font-bold ${textClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {profileImagePreview && !profileImage && user?.profileImage && (
                <div className="flex justify-center mb-4">
                  <button onClick={handleDeleteProfileImage} disabled={profileLoading}
                    className="px-3 py-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl text-xs font-bold disabled:opacity-50 flex items-center gap-1.5 transition-all duration-200"
                  >
                    <Trash weight="bold" className="w-3.5 h-3.5" />
                    Remove Image
                  </button>
                </div>
              )}

              {profileError && (
                <div className="mb-4 p-3 rounded-2xl bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 flex items-center gap-2">
                  <WarningCircle weight="bold" className="w-4 h-4 text-red-500 shrink-0" />
                  <p className="text-xs font-semibold text-red-600 dark:text-red-400">{profileError}</p>
                </div>
              )}
              {profileSuccess && (
                <div className="mb-4 p-3 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 flex items-center gap-2">
                  <Check weight="bold" className="w-4 h-4 text-emerald-500 shrink-0" />
                  <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">{profileSuccess}</p>
                </div>
              )}

              {/* Tab Navigation */}
              <div className={`flex gap-1 p-1 rounded-2xl ${darkMode ? 'bg-primaryB-700/50' : 'bg-primaryB-100'} mb-4`}>
                <button onClick={() => setActiveTab('profile')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wide transition-all duration-200 ${
                    activeTab === 'profile' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-elevated' : `${textClass} hover:bg-primaryB-50 dark:hover:bg-primaryB-600`
                  }`}
                >
                  <User weight="bold" className="w-3.5 h-3.5" />Profile
                </button>
                <button onClick={() => setActiveTab('security')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wide transition-all duration-200 ${
                    activeTab === 'security' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-elevated' : `${textClass} hover:bg-primaryB-50 dark:hover:bg-primaryB-600`
                  }`}
                >
                  <Lock weight="bold" className="w-3.5 h-3.5" />Security
                </button>
              </div>

              {activeTab === 'profile' && (
                <div className="space-y-3">
                  <div>
                    <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1 flex items-center gap-1.5`}>
                      <User weight="bold" className="w-3 h-3" />Full Name
                      <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-primaryB-600' : 'bg-primaryB-200'}`}>Read Only</span>
                    </label>
                    <input type="text" value={user?.fullName || ''} disabled
                      className={`w-full px-3 py-2.5 rounded-2xl border ${borderClass} ${darkMode ? 'bg-primaryB-700/50' : 'bg-primaryB-100'} ${mutedClass} text-sm cursor-not-allowed`}
                    />
                    <div className={`mt-1.5 p-2 rounded-xl ${darkMode ? 'bg-blue-900/30 border-blue-700/50' : 'bg-blue-50 border-blue-200'} border flex items-start gap-1.5`}>
                      <Info weight="bold" className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                      <p className={`text-[11px] ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Your name is managed by the business owner.</p>
                    </div>
                  </div>
                  <div>
                    <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1 flex items-center gap-1.5`}>
                      <span className="text-emerald-500">@</span>Username
                    </label>
                    <input type="text" value={profileData.username}
                      onChange={(e) => setProfileData(prev => ({ ...prev, username: e.target.value }))}
                      className={`w-full px-3 py-2.5 rounded-2xl border ${borderClass} ${surfaceClass} ${textClass} text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all duration-200 outline-none`}
                      placeholder="Enter username"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-3">
                  <div className={`p-3 rounded-2xl ${darkMode ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-emerald-50 border-emerald-200'} border flex items-center gap-2.5`}>
                    <Key weight="bold" className="w-4 h-4 text-emerald-600 shrink-0" />
                    <p className={`text-xs ${darkMode ? 'text-emerald-400/80' : 'text-emerald-700'}`}>Use at least 6 characters for your password.</p>
                  </div>
                  {[
                    { label: 'Current Password', field: 'currentPassword', show: showCurrentPassword, setShow: setShowCurrentPassword, placeholder: 'Enter current password' },
                    { label: 'New Password', field: 'newPassword', show: showNewPassword, setShow: setShowNewPassword, placeholder: 'Enter new password' },
                    { label: 'Confirm New Password', field: 'confirmPassword', show: showConfirmPassword, setShow: setShowConfirmPassword, placeholder: 'Confirm new password' },
                  ].map(({ label, field, show, setShow, placeholder }) => (
                    <div key={field}>
                      <label className={`text-[10px] font-bold uppercase ${mutedClass} block mb-1`}>{label}</label>
                      <div className="relative">
                        <input type={show ? 'text' : 'password'} value={profileData[field]}
                          onChange={(e) => setProfileData(prev => ({ ...prev, [field]: e.target.value }))}
                          className={`w-full px-3 py-2.5 pr-10 rounded-2xl border ${
                            field === 'confirmPassword' && profileData.confirmPassword && profileData.newPassword !== profileData.confirmPassword
                              ? 'border-red-500' : borderClass
                          } ${surfaceClass} ${textClass} text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all duration-200 outline-none`}
                          placeholder={placeholder}
                        />
                        <button type="button" onClick={() => setShow(!show)}
                          className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-xl ${mutedClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
                        >
                          {show ? <EyeSlash weight="bold" className="w-4 h-4" /> : <Eye weight="bold" className="w-4 h-4" />}
                        </button>
                      </div>
                      {field === 'confirmPassword' && profileData.confirmPassword && profileData.newPassword !== profileData.confirmPassword && (
                        <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><WarningCircle weight="bold" className="w-3 h-3" />Passwords do not match</p>
                      )}
                      {field === 'confirmPassword' && profileData.confirmPassword && profileData.newPassword === profileData.confirmPassword && profileData.newPassword && (
                        <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1"><Check weight="bold" className="w-3 h-3" />Passwords match</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`px-4 sm:px-6 py-3 border-t ${borderClass} flex items-center justify-end gap-2 ${darkMode ? 'bg-primaryB-800/50' : 'bg-primaryB-50'}`}>
              <button onClick={() => setShowProfileModal(false)}
                className={`px-5 py-2 ${surfaceClass} border ${borderClass} rounded-2xl text-xs font-bold ${textClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
              >
                Cancel
              </button>
              <button onClick={handleSaveProfile} disabled={profileLoading}
                className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-2xl text-xs font-bold shadow-elevated shadow-emerald-500/25 disabled:opacity-50 flex items-center justify-center gap-1.5 transition-all duration-200"
              >
                {profileLoading ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
                ) : (
                  <><FloppyDisk weight="bold" className="w-4 h-4" />Save Changes</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* More Bottom Sheet */}
      {moreSheetOpen && createPortal(
        <div className="lg:hidden fixed inset-0 z-[60]" onClick={() => setMoreSheetOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className={`absolute bottom-0 left-0 right-0 ${surfaceClass} rounded-t-3xl max-h-[70vh] overflow-y-auto safe-area-bottom`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-primaryB-600' : 'bg-primaryB-300'}`} />
            </div>
            <div className="px-5 pb-6">
              <div className="grid grid-cols-4 gap-3 mb-4">
                {navItems.filter(item => {
                  const tabPaths = [
                    `/${tenantSlug}/manager/dashboard`,
                    `/${tenantSlug}/manager/sales`,
                    `/${tenantSlug}/manager/inventory`,
                  ];
                  return !tabPaths.includes(item.path);
                }).map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  return (
                    <button key={item.path}
                      onClick={() => { navigate(item.path); setMoreSheetOpen(false); }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-all duration-200 ${
                        active
                          ? `${darkMode ? 'bg-accent-900/30' : 'bg-accent-50'} text-accent-500`
                          : `${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} ${mutedClass}`
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-[10px] font-bold text-center leading-tight">{item.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className={`border-t ${borderClass} pt-3 space-y-2`}>
                <div className={`flex items-center rounded-xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} p-1`}>
                  <button onClick={() => setDarkMode(false)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${!darkMode ? `bg-primaryB-50 shadow-card ${textClass}` : mutedClass}`}
                  >
                    <Sun weight="bold" className="w-3.5 h-3.5" />Light
                  </button>
                  <button onClick={() => setDarkMode(true)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[10px] font-bold uppercase transition-all duration-200 ${darkMode ? `bg-primaryB-800 shadow-card-dark ${textClass}` : mutedClass}`}
                  >
                    <Moon weight="bold" className="w-3.5 h-3.5" />Dark
                  </button>
                </div>
                <button onClick={() => { setMoreSheetOpen(false); handleLogout(); }}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold uppercase text-negative-500 ${darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'} transition-all duration-200`}
                >
                  <SignOut weight="bold" className="w-4 h-4" />Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default ManagerLayout;
